"use client";

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import Cookies from "js-cookie";
import { fetchCountryCurrencyOptions } from "@/api-service/shipping";
import { CountryCurrencyOption } from "@/packages/types/shipping";
import { useCurrency } from "./CurrencyContext";

interface CountryContextType {
  selectedCountryId?: number;
  selectedCountryCode?: string;
  selectedCountryName?: string;
  countryOptions: CountryCurrencyOption[];
  loadingCountries: boolean;
  changeCountry: (option: CountryCurrencyOption) => void;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

export const CountryProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedCountryId, setSelectedCountryId] = useState<number | undefined>();
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | undefined>();
  const [selectedCountryName, setSelectedCountryName] = useState<string | undefined>();
  const [countryOptions, setCountryOptions] = useState<CountryCurrencyOption[]>([]);
  const [loadingCountries, setLoadingCountries] = useState<boolean>(true);

  const { code: activeCurrencyCode, changeCurrency } = useCurrency();
  const isSelfChangingRef = useRef(false);

  const changeCountry = useCallback((option: CountryCurrencyOption) => {
    isSelfChangingRef.current = true;
    setSelectedCountryId(option.countryId);
    setSelectedCountryCode(option.countryCode);
    setSelectedCountryName(option.countryName);

    localStorage.setItem("selected_country", JSON.stringify(option));
    Cookies.set("country_id", String(option.countryId), { expires: 365 });

    // Sync matching currency into CurrencyContext
    if (activeCurrencyCode?.toUpperCase() !== option.currencyCode.toUpperCase()) {
      changeCurrency({
        id: option.currencyId || undefined,
        code: option.currencyCode,
        symbol: option.currencySymbol,
        exchangeRate: option.exchangeRate,
      });
    }

    setTimeout(() => {
      isSelfChangingRef.current = false;
    }, 50);
  }, [activeCurrencyCode, changeCurrency]);


  // Sync country selection when active currency changes in standalone CurrencySelector
  useEffect(() => {
    if (isSelfChangingRef.current) return;
    if (!activeCurrencyCode || countryOptions.length === 0) return;

    let targetCountryId = selectedCountryId;
    if (!targetCountryId) {
      const stored = localStorage.getItem("selected_country");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          targetCountryId = parsed.countryId;
        } catch (e) {
          console.log(e)
          // ignore parse error
        }
      }
    }

    // Check if currently selected country (or stored country) already matches the active currency
    const currentCountryMatch = countryOptions.find(
      (c) => c.countryId === targetCountryId && c.currencyCode.toUpperCase() === activeCurrencyCode.toUpperCase()
    );

    // If current country does NOT match the new active currency, find the FIRST country matching this currency
    if (!currentCountryMatch) {
      const firstMatchingCountry = countryOptions.find(
        (c) => c.currencyCode.toUpperCase() === activeCurrencyCode.toUpperCase()
      );

      if (firstMatchingCountry && firstMatchingCountry.countryId !== selectedCountryId) {
        setSelectedCountryId(firstMatchingCountry.countryId);
        setSelectedCountryCode(firstMatchingCountry.countryCode);
        setSelectedCountryName(firstMatchingCountry.countryName);

        localStorage.setItem("selected_country", JSON.stringify(firstMatchingCountry));
        Cookies.set("country_id", String(firstMatchingCountry.countryId), { expires: 365 });
      }
    }
  }, [activeCurrencyCode, countryOptions, selectedCountryId]);



  useEffect(() => {
    let active = true;

    const storedCountry = localStorage.getItem("selected_country");
    if (storedCountry) {
      try {
        const parsed: CountryCurrencyOption = JSON.parse(storedCountry);
        setSelectedCountryId(parsed.countryId);
        setSelectedCountryCode(parsed.countryCode);
        setSelectedCountryName(parsed.countryName);
      } catch (err) {
        console.error("Invalid country data in localStorage", err);
      }
    }

    const loadOptions = async () => {
      try {
        const res = await fetchCountryCurrencyOptions();
        if (active && res?.data) {
          setCountryOptions(res.data);
          const currentStored = localStorage.getItem("selected_country");
          if (!currentStored && res.data.length > 0) {
            // Default to first option only if no country is stored
            changeCountry(res.data[0]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch country options in CountryContext", err);
      } finally {
        if (active) setLoadingCountries(false);
      }
    };


    loadOptions();

    return () => {
      active = false;
    };
  }, []);


  return (
    <CountryContext.Provider
      value={{
        selectedCountryId,
        selectedCountryCode,
        selectedCountryName,
        countryOptions,
        loadingCountries,
        changeCountry,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error("useCountry must be used inside CountryProvider");
  return ctx;
};
